import { Box, IconButton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons"

import { useDateTime } from "../../../../../../../localization/useDateTime"
import { WeekMenuProduct } from "../../../../weekMenuTypes"

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "flex",
        alignItems: "center",
    },
    label: {
        color: theme.palette.tailwind.slate[900],
        fontSize: ".875rem",
        fontWeight: 500,
        marginRight: "1rem",
    },
    arrowGroup: {
        display: "flex",
        gap: "0.5rem",
    },
    arrowButton: {
        width: "2rem",
        height: "2rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".5rem",
        backgroundColor: theme.palette.common.white,
        color: theme.palette.tailwind.slate[900],
        "&.Mui-disabled": {
            backgroundColor: theme.palette.tailwind.slate[200],
            borderColor: theme.palette.tailwind.slate[200],
            color: theme.palette.tailwind.slate[400],
        },
    },
    arrowIcon: {
        fontSize: ".875rem",
    },
}))

type DayWithProducts = Readonly<{
    date: string
    products: WeekMenuProduct[]
}>

type DaySelectorProps = Readonly<{
    weekMenus: DayWithProducts[]
    currentIndex: number
    onDaySelect: (index: number) => void
}>

enum NavigationDirection {
    PREVIOUS = "prev",
    NEXT = "next",
}

function findClosestIndexWithProducts(
    weekMenus: DayWithProducts[],
    startIndex: number,
    direction: NavigationDirection,
) {
    const step = direction === NavigationDirection.PREVIOUS ? -1 : 1

    for (let index = startIndex + step; index >= 0 && index < weekMenus.length; index += step) {
        if (weekMenus[index]?.products?.length) {
            return index
        }
    }

    return -1
}

export function DaySelector({ weekMenus, currentIndex, onDaySelect }: DaySelectorProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const currentMenu = weekMenus[currentIndex]
    const previousIndex = findClosestIndexWithProducts(weekMenus, currentIndex, NavigationDirection.PREVIOUS)
    const nextIndex = findClosestIndexWithProducts(weekMenus, currentIndex, NavigationDirection.NEXT)

    if (!currentMenu) return null

    const currentDate = dateTimeFactory.fromISO(currentMenu.date)
    const label = dateTimeFactory.formatCustom(currentDate, "EEEE dd/MM")

    return (
        <Box className={classes.container}>
            <Typography component="span" className={classes.label}>
                {label}
            </Typography>
            <Box className={classes.arrowGroup}>
                <NavigationButton
                    icon={faChevronLeft}
                    disabled={previousIndex === -1}
                    onClick={() => onDaySelect(previousIndex)}
                />
                <NavigationButton
                    icon={faChevronRight}
                    disabled={nextIndex === -1}
                    onClick={() => onDaySelect(nextIndex)}
                />
            </Box>
        </Box>
    )
}

type NavigationButtonProps = Readonly<{
    icon: typeof faChevronLeft
    disabled: boolean
    onClick: () => void
}>

function NavigationButton({ icon, disabled, onClick }: NavigationButtonProps) {
    const { classes } = useStyles()

    return (
        <IconButton className={classes.arrowButton} onClick={onClick} disabled={disabled}>
            <FontAwesomeIcon icon={icon} className={classes.arrowIcon} />
        </IconButton>
    )
}
